import React from 'react';
import { Card } from '@/components/ui/card';

export default function PrivacyPolicyPage() {

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-4 pb-24">
      <div className="max-w-3xl mx-auto">
        <Card className="p-8 glass prose prose-teal max-w-none">
          <h1 className="text-4xl md:text-5xl font-extrabold text-teal-900 mb-6">Privacy Policy</h1>
          <p className="text-gray-500">Last updated: February 19, 2026</p>

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">1. Introduction</h2>
          <p>Welcome to 12challenges.com ("Service", "we", "our", or "us").</p>
          <p>We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Service.</p>
          <p>This Privacy Policy is intended to comply with applicable privacy laws, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>General Data Protection Regulation (GDPR)</li>
            <li>California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act (CPRA)</li>
            <li>California Online Privacy Protection Act (CalOPPA)</li>
            <li>Children's Online Privacy Protection Act (COPPA)</li>
          </ul>
          <p>By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">2. Information We Collect</h2>
          <p>We collect only the minimum personal information necessary to operate the Service.</p>

          <h3 className="text-xl font-bold mt-4 mb-2 text-gray-800">Personal Information</h3>
          <p>We may collect:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Email address</li>
            <li>First name and last name (if provided)</li>
            <li>Content you upload, submit, or create within the Service</li>
          </ul>

          <h3 className="text-xl font-bold mt-4 mb-2 text-gray-800">Automatically Collected Information</h3>
          <p>We may automatically collect limited technical information, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>IP address</li>
            <li>Browser type</li>
            <li>Device information</li>
            <li>Login timestamps</li>
          </ul>
          <p>This information is used solely for security, authentication, and operation of the Service.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">3. How We Use Your Information</h2>
          <p>We use your information only to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Provide and operate the Service</li>
            <li>Create and manage user accounts</li>
            <li>Authenticate users</li>
            <li>Store and display user content</li>
            <li>Maintain security and prevent abuse</li>
            <li>Respond to user inquiries</li>
          </ul>
          <p>We do not sell your personal information.</p>
          <p>We do not use your personal information for advertising.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">4. Legal Basis for Processing (GDPR)</h2>
          <p>If you are located in the European Economic Area (EEA) or United Kingdom, we process your personal data based on:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Your consent when you register and use the Service</li>
            <li>Our legitimate interest in operating and securing the Service</li>
          </ul>
          <p>You may withdraw consent at any time by deleting your account or contacting us.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">5. Data Retention</h2>
          <p>We retain personal data only as long as necessary to provide the Service.</p>
          <p>If you delete your account, your personal data is permanently deleted from our active systems, except where retention is required for legitimate legal or security purposes.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">6. Your Rights Under GDPR</h2>
          <p>If you are in the EU or UK, you have the right to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Access your personal data</li>
            <li>Correct inaccurate personal data</li>
            <li>Request deletion of your personal data</li>
            <li>Restrict processing</li>
            <li>Object to processing</li>
            <li>Request data portability</li>
            <li>Withdraw consent</li>
          </ul>
          <p>To exercise these rights, contact: <a href="mailto:federicson7@gmail.com" className="text-teal-600 hover:underline">federicson7@gmail.com</a></p>
          <p>You also have the right to lodge a complaint with your local data protection authority.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">7. Your Rights Under CCPA and CPRA (California Residents)</h2>
          <p>If you are a California resident, you have the right to:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Know what personal information we collect</li>
            <li>Request access to your personal information</li>
            <li>Request deletion of your personal information</li>
            <li>Correct inaccurate personal information</li>
            <li>Limit use of sensitive personal information (if applicable)</li>
            <li>Not be discriminated against for exercising your rights</li>
          </ul>
          <p>We do not sell or share personal information.</p>
          <p>To exercise your rights, contact: <a href="mailto:federicson7@gmail.com" className="text-teal-600 hover:underline">federicson7@gmail.com</a></p>
          <p>We will respond within the time required by applicable law.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">8. CalOPPA Compliance (California Online Privacy Protection Act)</h2>
          <p>In compliance with CalOPPA:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Users can visit our Service anonymously without creating an account</li>
            <li>This Privacy Policy is clearly accessible from our website</li>
            <li>Users can request changes or deletion of their personal data by contacting us</li>
            <li>We do not track users across third-party websites for behavioral tracking</li>
            <li>We do not respond to Do Not Track (DNT) signals because we do not perform cross-site tracking</li>
          </ul>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">9. Children's Privacy (COPPA Compliance)</h2>
          <p>The Service is not intended for children under the age of 13.</p>
          <p>You must be at least 13 years old to use the Service.</p>
          <p>We do not knowingly collect personal information from children under 13.</p>
          <p>If we become aware that personal information from a child under 13 has been collected, we will delete it immediately.</p>
          <p>If you believe such data exists, contact: <a href="mailto:federicson7@gmail.com" className="text-teal-600 hover:underline">federicson7@gmail.com</a></p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">10. Sharing of Information</h2>
          <p>We do not sell, rent, or trade your personal information.</p>
          <p>We may share information only with service providers necessary to operate the Service (such as hosting providers), who process data solely on our behalf and under confidentiality obligations.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">11. Data Security</h2>
          <p>We implement reasonable technical and organizational safeguards, including:</p>
          <ul className="list-disc pl-6 mb-4 space-y-1">
            <li>Secure HTTPS connections</li>
            <li>Password authentication protections</li>
            <li>Restricted access to personal data</li>
          </ul>
          <p>However, no system is completely secure.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">12. Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time.</p>
          <p>Changes will be posted on this page with an updated "Last updated" date.</p>
          <p>Continued use of the Service constitutes acceptance of any changes.</p>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mt-8 mb-4 text-teal-800">13. Contact Information</h2>
          <p>If you have questions, requests, or concerns about this Privacy Policy or your personal data, contact:</p>
          <p><a href="mailto:federicson7@gmail.com" className="text-teal-600 hover:underline">federicson7@gmail.com</a></p>
        </Card>
      </div>
    </div>
  );
}
